/**
 * @file How a report shows each query it may hold: a title per query and, per returned column, a
 * label and the format kind its cells take. Read only by the report renderers: the React run view,
 * the Reports list sub-line (query titles joined) and the PDF; the tab screens keep their own view
 * models. A title holding `<team>` names the team noun, which the renderer fills from the scope labels.
 * A stored column the registry does not name renders with its id as header and its raw cell; a
 * registry column absent from a stored run is skipped. A `cloud_run/analytics` test ties every
 * query's columns to its body's output, both ways. A `line`-prefixed column is the whole amount
 * beside its credited share. A team column is named `ANALYTICS_TEAM_ROW_COLUMN`, the one row-per-team
 * output column.
 */
import { ANALYTICS_TEAM_ROW_COLUMN } from './Analytics.NamedQuery.types';
import type { AnalyticsReportQueryId } from './Analytics.Report.types';

export type AnalyticsColumnFormat = 'currency' | 'count' | 'date' | 'person' | 'team' | 'text' | 'agingBucket' | 'stageList';

export type NamedQueryColumnPresentation = { label: string; format: AnalyticsColumnFormat };

export type NamedQueryPresentation = { title: string; columns: Readonly<Record<string, NamedQueryColumnPresentation>> };

const column = (label: string, format: AnalyticsColumnFormat): NamedQueryColumnPresentation => ({ label, format });

const SALESPERSON = column('Salesperson', 'person');
const TEAM = column('Team', 'team');
const STAGES = column('Stage', 'stageList');
const MONTH = column('Month', 'text');
const KIND = column('Kind', 'text');
const QUOTES = column('Quotes', 'count');
const QUOTED = column('Quoted', 'currency');
const BOOKED = column('Booked', 'currency');
const SALES_CREDIT = column('Sales credit', 'currency');
const PROJECT = column('Project', 'text');
const PROJECT_NAME = column('Project name', 'text');
const FORM = column('Form', 'text');
const AGE = column('Age', 'agingBucket');
const STATUS = column('Status', 'text');
const INVOICES = column('Invoices', 'count');
const OVERDUE_INVOICES = column('Overdue invoices', 'count');

const valued = (label: string, credited: string, whole: string): Readonly<Record<string, NamedQueryColumnPresentation>> => ({
  [credited]: column(label, 'currency'),
  [whole]: column(`${label} (whole amount)`, 'currency'),
});

const INVOICED = valued('Invoiced', 'invoicedAmount', 'lineInvoicedAmount');
const OUTSTANDING = valued('Outstanding', 'outstandingAmount', 'lineOutstandingAmount');

export const NAMED_QUERY_PRESENTATION = {
  salesClosedByPerson: { title: 'Booked by salesperson', columns: { salespersonMemberId: SALESPERSON, amount: BOOKED, creditAmount: SALES_CREDIT } },
  salesClosedByTeam: {
    title: 'Booked by <team>',
    columns: {
      [ANALYTICS_TEAM_ROW_COLUMN]: TEAM,
      amount: BOOKED,
      creditAmount: SALES_CREDIT,
      teamSalespeople: column('Salespeople', 'count'),
      totalSalespeople: column('Salespeople in total', 'count'),
    },
  },
  salesClosedTotal: { title: 'Booked', columns: { amount: BOOKED, creditAmount: SALES_CREDIT } },
  salesClosedByMonth: { title: 'Booked by month', columns: { month: MONTH, amount: BOOKED, creditAmount: SALES_CREDIT } },
  quoteCountAndValueByKind: { title: 'Quotes by kind', columns: { kind: KIND, quotes: QUOTES, quotedAmount: QUOTED } },
  quoteValueTotal: { title: 'Quoted', columns: { stages: STAGES, quotedAmount: QUOTED } },
  quoteValueByMonth: { title: 'Quoted by month', columns: { month: MONTH, stages: STAGES, quotedAmount: QUOTED } },
  quoteValueByPerson: { title: 'Quoted by salesperson', columns: { salespersonMemberId: SALESPERSON, stages: STAGES, quotedAmount: QUOTED } },
  quoteValueByTeam: { title: 'Quoted by <team>', columns: { [ANALYTICS_TEAM_ROW_COLUMN]: TEAM, stages: STAGES, quotedAmount: QUOTED } },
  releaseValueByState: { title: 'Held and released', columns: { state: column('State', 'text'), orderedAmount: column('Ordered', 'currency') } },
  openQuotesAndBuysByProject: {
    title: 'Open quotes and buys by project',
    columns: { projectId: PROJECT, stages: STAGES, forms: column('Forms', 'count'), quotedAmount: QUOTED },
  },
  conversionByKind: { title: 'Conversion by kind', columns: { kind: KIND, quotes: QUOTES, converted: column('Converted', 'count') } },
  pipelineByStage: {
    title: 'Pipeline by stage',
    columns: {
      stage: column('Form type', 'text'),
      released: column('Released', 'text'),
      forms: column('Forms', 'count'),
      projects: column('Projects', 'count'),
      value: column('Value', 'currency'),
    },
  },
  orderLinesTotals: {
    title: 'Order lines',
    columns: {
      openLines: column('Open lines', 'count'),
      openForms: column('Open forms', 'count'),
      heldLines: column('Held lines', 'count'),
      ...valued('Held', 'heldValue', 'lineHeldValue'),
      releasedLines: column('Released lines', 'count'),
      ...valued('Released', 'releasedValue', 'lineReleasedValue'),
      shippedLines: column('Shipped lines', 'count'),
      ...valued('Shipped', 'shippedValue', 'lineShippedValue'),
      pastPromisedLines: column('Past promised lines', 'count'),
      ...valued('Value at risk', 'valueAtRisk', 'lineValueAtRisk'),
      ...valued('Shipped not invoiced', 'shippedNotInvoicedValue', 'lineShippedNotInvoicedValue'),
    },
  },
  orderLinesList: {
    title: 'Order line list',
    columns: {
      docPath: column('Line', 'text'),
      formId: FORM,
      formNumber: column('Form number', 'text'),
      projectId: PROJECT,
      projectName: PROJECT_NAME,
      itemType: column('Type', 'text'),
      itemDescription: column('Description', 'text'),
      manufacturerName: column('Manufacturer', 'text'),
      quantity: column('Quantity', 'count'),
      lineItemStatus: STATUS,
      ...valued('Ordered', 'orderedAmount', 'lineOrderedAmount'),
      promisedDate: column('Promised', 'date'),
      pastPromised: column('Past promised', 'text'),
      totalLines: column('Lines in total', 'count'),
    },
  },
  invoiceTotals: {
    title: 'Invoice totals',
    columns: {
      invoicedInvoices: INVOICES,
      ...INVOICED,
      outstandingInvoices: column('Open invoices', 'count'),
      ...OUTSTANDING,
      overdueInvoices: OVERDUE_INVOICES,
      ...valued('Overdue', 'overdueAmount', 'lineOverdueAmount'),
    },
  },
  receivableAging: { title: 'Receivable aging', columns: { bucket: AGE, invoices: INVOICES, ...OUTSTANDING } },
  invoiceList: {
    title: 'Invoices',
    columns: {
      formId: FORM,
      invoiceNumber: column('Invoice number', 'text'),
      projectId: PROJECT,
      projectName: PROJECT_NAME,
      invoiceDate: column('Invoice date', 'date'),
      invoiceDueDate: column('Due date', 'date'),
      invoiceStatus: STATUS,
      bucket: AGE,
      ...INVOICED,
      ...OUTSTANDING,
      totalInvoices: column('Invoices in total', 'count'),
    },
  },
  overdueInvoicesBySummaryScope: {
    title: 'Overdue invoices',
    columns: { groupingSet: column('Grouping', 'text'), salespersonMemberId: SALESPERSON, overdueInvoices: OVERDUE_INVOICES },
  },
} as const satisfies Record<AnalyticsReportQueryId, NamedQueryPresentation>;
